import React, { useMemo } from 'react';
import {
  FieldContext,
  FilterType,
  IBasicFilterFieldsFragment,
  IFilterFieldsFragment,
  ILogicalOperatorFieldsFragment,
  IPredicateFieldsFragment,
} from '../../../../../../utils/apollo/graphqlSchema';
import * as DashboardFilterViewUI from './DashboardFilterViewUI';
import {
  useActionsVisibilityContext,
  withActionsVisibilityContext,
} from '../../../../../../components/ActionsVisibilityContext';
import { SvgIcon } from '../../../../../../components/Icon';
import BasicFilterPredicate from '../../../../../../components/FilterFields/FilterFieldsView/BasicFilterView/BasicFilterCondition/BasicFilterPredicate';
import FilterWidget from '../../../../../../components/FilterWidget';
import { IPredicateContext } from '../../../../../../components/FilterWidget/types';
import { defaultEventInput } from '../../../../../../utils/builder';
import useDashboardSchemaId from '../../../../utils/useDashboardSchemaId';
import Popover from '../../../../../../components/Popover';
import usePopoverController from '../../../../../../components/Popover/usePopoverController';
import { assertCondition, assertNotNil } from '../../../../../../utils/types';

interface IProps {
  predicate: IPredicateFieldsFragment;
  logicOp: ILogicalOperatorFieldsFragment;
  keyPrefix: string;
  onRemovePredicate: () => void;
  onChange: (newPredicate: IPredicateFieldsFragment) => void;
  includeAnyValue: boolean;
}

const predicateContext: IPredicateContext = {
  fieldContext: FieldContext.Filter,
  events: [defaultEventInput],
  func: null,
};

const DashboardFilterEditablePredicate = ({
  predicate,
  logicOp,
  keyPrefix,
  onRemovePredicate,
  onChange,
  includeAnyValue,
}: IProps) => {
  const { openPopover, closePopover, isPopoverOpen, popoverAnchor } =
    usePopoverController<HTMLDivElement>();
  const schemaId = useDashboardSchemaId();
  assertNotNil(schemaId);
  const { actionsVisible, showActions, hideActions } =
    useActionsVisibilityContext();

  const handleRemovePredicate = (ev: React.MouseEvent<HTMLDivElement>) => {
    ev.stopPropagation();
    onRemovePredicate();
    hideActions();
  };

  const handleFilterSubmit = (newFilter: IFilterFieldsFragment) => {
    assertCondition(newFilter.__typename === 'BasicFilter');
    assertCondition(newFilter.conditions.length === 1);
    assertCondition(newFilter.conditions[0].predicates.length === 1);
    onChange(newFilter.conditions[0].predicates[0]);
    closePopover();
  };

  const defaultFilter = useMemo<IBasicFilterFieldsFragment>(
    () => ({
      __typename: 'BasicFilter',
      type: FilterType.Basic,
      conditions: [
        {
          __typename: 'Condition',
          predicates: [predicate],
          logicOps: [],
        },
      ],
      logicOps: [],
    }),
    [predicate],
  );

  return (
    <>
      <DashboardFilterViewUI.PredicateRow>
        <DashboardFilterViewUI.EditableLabel
          ref={popoverAnchor}
          onMouseEnter={showActions}
          onMouseLeave={hideActions}
          onClick={openPopover}
          $isActionsVisible={actionsVisible}
        >
          <BasicFilterPredicate predicate={predicate} keyPrefix={keyPrefix} />
          {actionsVisible && (
            <DashboardFilterViewUI.ActionContainer
              onClick={handleRemovePredicate}
            >
              <SvgIcon name="icon-x" width={8} height={8} />
            </DashboardFilterViewUI.ActionContainer>
          )}
        </DashboardFilterViewUI.EditableLabel>

        {logicOp && (
          <DashboardFilterViewUI.LogicOp>
            {logicOp.key}
          </DashboardFilterViewUI.LogicOp>
        )}
      </DashboardFilterViewUI.PredicateRow>
      <Popover
        anchor={popoverAnchor.current}
        isOpen={isPopoverOpen}
        close={closePopover}
      >
        <FilterWidget
          onSubmit={handleFilterSubmit}
          schemaId={schemaId}
          context={predicateContext}
          defaultFilter={defaultFilter}
          defaultLogOps={[]}
          maxConditions={1}
          maxPredicates={1}
          includeAnyValue={includeAnyValue}
          disableNamedFilters={true}
        />
      </Popover>
    </>
  );
};

export default withActionsVisibilityContext(DashboardFilterEditablePredicate);
